import type { LngLat } from '@/composables/useLocation'
import { env } from '@/config/env'
import { FLOW, track } from '@/utils/track'

/** Подсказка адреса: заголовок и уточнение — ровно две строки из макета. */
export interface AddressHint {
  id: string
  title: string
  subtitle: string
  /** Полный адрес одной строкой — запасной вариант, если геокодер промолчит */
  value: string
  /** Ссылка на объект геокодера: по ней достаём и точку, и канонический адрес */
  uri: string
}

/** Найденная точка: координаты и адрес одной строкой. */
export interface GeoPlace {
  point: LngLat
  address: string
  /**
   * Номер дома отдельной строкой — геокодер разбирает адрес по частям.
   * Пусто, когда Яндекс дома не знает: у пина посреди двора или махалли
   * в ответе есть только улица, и дом клиенту придётся вписать самому.
   */
  house: string
}

const SUGGEST_LIMIT = 10

/**
 * Окно поиска вокруг центра региона, в градусах. ~0.6° — это примерно
 * 60 км: город с пригородами внутрь попадает, соседняя область уже нет.
 */
const REGION_SPAN: LngLat = [0.6, 0.6]

const GEOCODER_URL = 'https://geocode-maps.yandex.ru/1.x/'

/**
 * Подсказки адресов (Suggest API). `center` со `span` не режут выдачу
 * жёстко, но поднимают наверх объекты своего региона: без них на «чиланзар 19»
 * вперемешку приезжают Самарканд и Шахрисабз.
 *
 * Падает молча: до загрузки скрипта карты `ymaps3` ещё нет.
 */
export async function yandexSuggest(text: string, center?: LngLat): Promise<AddressHint[]> {
  /* Каждый вызов — платный запрос к Яндексу; по этим строкам виден расход ключа */
  track(FLOW.yandexSuggest, { length: text.length })

  try {
    const found = await ymaps3.suggest({
      text,
      limit: SUGGEST_LIMIT,
      ...(center ? { center, span: REGION_SPAN } : {}),
    })
    return found.map((item, index) => ({
      id: item.uri ?? `${index}`,
      title: item.title.text,
      subtitle: item.subtitle?.text ?? item.address?.formattedAddress ?? '',
      /* Яндекс отдаёт адрес с висячим пробелом на конце */
      value: (item.address?.formattedAddress ?? item.value).trim(),
      uri: item.uri ?? item.value,
    }))
  } catch {
    return []
  }
}

/** Часть разобранного адреса: `kind` — что это, `name` — как называется. */
interface AddressComponent {
  kind: string
  name: string
}

/** Ответ геокодера — берём из него адрес, координаты и разбор по частям. */
interface GeocoderResponse {
  response: {
    GeoObjectCollection: {
      featureMember: {
        GeoObject: {
          Point?: { pos: string }
          description?: string
          metaDataProperty: {
            GeocoderMetaData: {
              text: string
              Address?: { Components?: AddressComponent[] }
            }
          }
        }
      }[]
    }
  }
}

/** Номер дома из разбора адреса; у улицы или махалли такой части нет. */
function houseOf(components?: AddressComponent[]): string {
  return components?.find((item) => item.kind === 'house')?.name ?? ''
}

/** Геокодер ставит страну первой: «Узбекистан, Ташкент, …» — она лишняя. */
function stripCountry(text: string, description?: string): string {
  const country = description?.split(',').pop()?.trim()
  if (!country) return text
  return text.startsWith(`${country}, `) ? text.slice(country.length + 2) : text
}

/**
 * Геокодер Яндекса. Это отдельная от карты служба со своим HTTP API —
 * `ymaps3.search` из JS API ходит в Search API, а он подключается отдельно
 * и на ключе бренда может отсутствовать.
 */
async function geocode(params: Record<string, string>): Promise<GeoPlace | null> {
  if (!env.mapKey) return null

  /* По координатам — пин на карте, по `uri` — выбранная подсказка */
  const kind = params.geocode ? 'reverse' : 'hint'

  const query = new URLSearchParams({
    format: 'json',
    apikey: env.mapKey,
    lang: 'ru_RU',
    results: '1',
    ...params,
  })

  /* Считаем запрос ровно один раз, чем бы он ни кончился: 0 — связь оборвалась */
  let status = 0

  try {
    const response = await fetch(`${GEOCODER_URL}?${query}`)
    status = response.status
    if (!response.ok) return null

    const body = (await response.json()) as GeocoderResponse
    const object = body.response.GeoObjectCollection.featureMember[0]?.GeoObject
    if (!object) return null

    const [longitude, latitude] = (object.Point?.pos ?? '').split(' ').map(Number)
    const meta = object.metaDataProperty.GeocoderMetaData
    const address = stripCountry(meta.text, object.description)

    return {
      point: [longitude ?? 0, latitude ?? 0],
      address,
      house: houseOf(meta.Address?.Components),
    }
  } catch {
    return null
  } finally {
    track(FLOW.yandexGeocode, { kind, status })
  }
}

/** Точка и канонический адрес выбранной подсказки — по её `uri`. */
export async function resolveHint(hint: AddressHint): Promise<GeoPlace | null> {
  const place = await geocode({ uri: hint.uri })
  if (!place) return null
  return { ...place, address: place.address || hint.value }
}

/** Адрес по координатам — для точки, поставленной руками на карте. */
export async function reverseGeocode(coords: LngLat): Promise<GeoPlace | null> {
  return geocode({ geocode: coords.join(',') })
}
